import React, { useEffect, useState } from "react";
import { useParams,useNavigate } from "react-router-dom";
import api from "../services/api";
import "./DetalleProyecto.css";
import PresupuestoDetallado from "../pages/PresupuestoDetallado";
import { useFiscal } from '../hooks/useFiscal';

const DetalleProyecto = () => {
  const fiscal = useFiscal();
  const { id } = useParams();
  const navigate = useNavigate();
  const [proyecto, setProyecto] = useState(null);
  const [salidas, setSalidas] = useState([]);
  const [consumoMateriales, setConsumoMateriales] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
 const [vistaActiva, setVistaActiva] = useState("salidas"); // salidas | materiales | presupuesto | info
  const [salidaSeleccionada, setSalidaSeleccionada] = useState(null); // Para el modal
  const [certificaciones, setCertificaciones] = useState([]);
  const [resumenCert, setResumenCert] = useState(null);

  useEffect(() => {
    cargarDatos();
  }, [id]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      
      const [resProyecto, resSalidas, resConsumo,resCert] = await Promise.all([
        api.get(`/proyectos/${id}`),
        api.get(`/proyectos/${id}/salidas`),
        api.get(`/proyectos/${id}/consumo-materiales`),
        api.get(`/proyectos/${id}/certificaciones`)
      ]);

      setProyecto(resProyecto.data);
      setSalidas(resSalidas.data.salidas || []);
      setResumen(resSalidas.data.resumen);
      setConsumoMateriales(resConsumo.data.materiales || []);
      setCertificaciones(resCert.data.certificaciones || []);
      setResumenCert(resCert.data.resumen);
    } catch (error) {
      console.error("Error cargando datos del proyecto:", error);
    } finally {
      setLoading(false);
    }
  };

  const sincronizarPresupuesto = async () => {
    try {
      await api.post(`/proyectos/${id}/sincronizar-presupuesto`);
      cargarDatos(); // Recargar datos
      alert("Presupuesto sincronizado exitosamente");
    } catch (error) {
      console.error("Error sincronizando presupuesto:", error);
      alert("Error al sincronizar presupuesto");
    }
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return "-";
    return new Date(fecha).toLocaleDateString("es-ES", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const formatearMoneda = (valor) => {
    return new Intl.NumberFormat("es-DO", {
      style: "currency",
      currency: fiscal.moneda?.codigo || "DOP"
    }).format(valor || 0);
  };

  const getEstadoColor = (estado) => {
    const colores = {
      activo: "#10b981",
      pausado: "#f59e0b",
      completado: "#3b82f6",
      cancelado: "#ef4444",
      aprobada: "#10b981",
      pendiente_aprobacion: "#f59e0b",
      rechazada: "#ef4444"
    };
    return colores[estado] || "#6b7280";
  };

  const getPorcentajePresupuesto = () => {
    if (!proyecto || !proyecto.presupuesto) return 0;
    return (proyecto.presupuesto_gastado / proyecto.presupuesto) * 100;
  };

  if (loading) {
    return (
      <div className="detalle-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Cargando proyecto...</p>
        </div>
      </div>
    );
  }

  if (!proyecto) {
    return (
      <div className="detalle-container">
        <div className="error-message">
          <p>Proyecto no encontrado</p>
          <a href="/proyectos" className="btn-primary">
            Volver a Proyectos
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="detalle-container">
      {/* Header con info del proyecto */}
      <div className="detalle-header">
        <div className="header-info">
          <a href="/proyectos" className="btn-back">
            ← Volver
          </a>
          <h1>{proyecto.nombre}</h1>
          <div className="proyecto-meta">
            <span
              className="badge"
              style={{ backgroundColor: getEstadoColor(proyecto.estado) }}
            >
              {proyecto.estado}
            </span>
            {proyecto.ubicacion && (
              <span className="meta-item">
                📍 {proyecto.ubicacion}
              </span>
            )}
            {proyecto.responsable && (
              <span className="meta-item">
                👤 {proyecto.responsable}
              </span>
            )}
          </div>
        </div>
        <div className="header-actions">
          <button onClick={sincronizarPresupuesto} className="btn-secondary">
            🔄 Sincronizar Presupuesto
          </button>
          <a href={`/proyectos/editar/${id}`} className="btn-primary">
            ✏️ Editar Proyecto
          </a>
        </div>
      </div>

      {/* Resumen de presupuesto */}
      <div className="presupuesto-card">
        {/* Valor del Contrato */}
        {proyecto.valor_contrato > 0 && (
          <div className="valor-contrato-header">
            <span className="contrato-icon">💰</span>
            <div className="contrato-info">
              <span className="contrato-label">Valor del Contrato</span>
              <span className="contrato-valor">{formatearMoneda(proyecto.valor_contrato)}</span>
            </div>
          </div>
        )}
        
        <div className="presupuesto-info">
          <div className="presupuesto-item">
            <span className="label">Presupuesto Materiales</span>
            <span className="valor total">
              {formatearMoneda(proyecto.presupuesto)}
            </span>
          </div>
          <div className="presupuesto-item">
            <span className="label">Gastado</span>
            <span className="valor gastado">
              {formatearMoneda(proyecto.presupuesto_gastado)}
            </span>
          </div>
          <div className="presupuesto-item">
            <span className="label">Disponible</span>
            <span className="valor disponible">
              {formatearMoneda(proyecto.presupuesto - proyecto.presupuesto_gastado)}
            </span>
          </div>
          <div className="presupuesto-item">
            <span className="label">% Utilizado</span>
            <span className="valor porcentaje">
              {getPorcentajePresupuesto().toFixed(1)}%
            </span>
          </div>
        </div>
        <div className="presupuesto-bar">
          <div
            className="presupuesto-fill"
            style={{
              width: `${Math.min(getPorcentajePresupuesto(), 100)}%`,
              backgroundColor: getPorcentajePresupuesto() > 90 ? "#ef4444" : "#3b82f6"
            }}
          ></div>
        </div>
      </div>

      {/* Tabs de navegación */}
      <div className="tabs-container">
        <button
          className={`tab ${vistaActiva === "salidas" ? "active" : ""}`}
          onClick={() => setVistaActiva("salidas")}
        >
          📦 Salidas ({resumen?.total_salidas || 0})
        </button>
        <button
          className={`tab ${vistaActiva === "materiales" ? "active" : ""}`}
          onClick={() => setVistaActiva("materiales")}
        >
          🧱 Materiales ({consumoMateriales.length})
        </button>
       <button
    className={`tab ${vistaActiva === "presupuesto" ? "active" : ""}`}
    onClick={() => setVistaActiva("presupuesto")}
  >
    📋 Presupuesto Detallado
  </button>
  <button
    className={`tab ${vistaActiva === "certificaciones" ? "active" : ""}`}
    onClick={() => setVistaActiva("certificaciones")}
  >
    📜 Certificaciones
  </button>
  <button
    className={`tab ${vistaActiva === "info" ? "active" : ""}`}
    onClick={() => setVistaActiva("info")}
  >
    ℹ️ Información
  </button>
</div>

      {/* Contenido según tab activa */}
      <div className="tab-content">
        {vistaActiva === "salidas" && (
          <div className="salidas-section">
            {resumen && (
              <div className="resumen-salidas">
                <div className="resumen-item">
                  <span className="icon">✅</span>
                  <div>
                    <span className="numero">{resumen.aprobadas}</span>
                    <span className="texto">Aprobadas</span>
                  </div>
                </div>
                <div className="resumen-item">
                  <span className="icon">⏳</span>
                  <div>
                    <span className="numero">{resumen.pendientes}</span>
                    <span className="texto">Pendientes</span>
                  </div>
                </div>
                <div className="resumen-item">
                  <span className="icon">❌</span>
                  <div>
                    <span className="numero">{resumen.rechazadas}</span>
                    <span className="texto">Rechazadas</span>
                  </div>
                </div>
                <div className="resumen-item valor">
                  <span className="icon">💰</span>
                  <div>
                    <span className="numero">{formatearMoneda(resumen.valor_total_gastado)}</span>
                    <span className="texto">Valor Total</span>
                  </div>
                </div>
              </div>
            )}

            {salidas.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">📭</span>
                <h3>No hay salidas registradas</h3>
                <p>Aún no se han registrado salidas para este proyecto</p>
              </div>
            ) : (
              <div className="tabla-container">
                <table className="tabla-salidas">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Material</th>
                      <th>Cantidad</th>
                      <th>Valor</th>
                      <th>Responsable</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salidas.map((salida) => (
                      <tr key={salida.id}>
                     <td>{formatearFecha(salida.fecha_aprobacion || salida.fecha)}</td>
                        <td>
                          <div className="material-info">
                            <strong>{salida.material?.nombre}</strong>
                            <span className="categoria">
                              {salida.material?.categoria?.nombre}
                            </span>
                          </div>
                        </td>
                        <td className="cantidad">{salida.cantidad}</td>
                        <td className="valor">{formatearMoneda(salida.valor_total)}</td>
                        <td>{salida.responsable || "-"}</td>
                        <td>
                          <span
                            className="badge-mini"
                            style={{ backgroundColor: getEstadoColor(salida.estado) }}
                          >
                            {salida.estado}
                          </span>
                        </td>
                        <td>
                          <button 
                            onClick={() => setSalidaSeleccionada(salida)}
                            className="btn-mini"
                            style={{ background: '#f3f4f6', border: 'none', cursor: 'pointer' }}
                          >
                            👁️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {vistaActiva === "materiales" && (
          <div className="materiales-section">
            {consumoMateriales.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">🧱</span>
                <h3>No hay materiales consumidos</h3>
                <p>Aún no se han consumido materiales en este proyecto</p>
              </div>
            ) : (
              <div className="materiales-grid">
                {consumoMateriales.map((material, index) => (
                  <div key={index} className="material-card">
                    <div className="material-header">
                      <h4>{material.material_nombre}</h4>
                      <span className="categoria-badge">
                        {material.categoria}
                      </span>
                    </div>
                    <div className="material-stats">
                      <div className="stat">
                        <span className="stat-label">Cantidad Total</span>
                        <span className="stat-value">
                          {material.cantidad_total} {material.unidad}
                        </span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Valor Total</span>
                        <span className="stat-value valor">
                          {formatearMoneda(material.valor_total)}
                        </span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Nº Salidas</span>
                        <span className="stat-value">
                          {material.numero_salidas}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {vistaActiva === "presupuesto" && (
  <div className="presupuesto-section">
    <PresupuestoDetallado />
  </div>
)}

{vistaActiva === "certificaciones" && (
          <div className="certificaciones-section">
            {certificaciones.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📜</div>
                <h3>No hay certificaciones</h3>
                <p>Este proyecto aún no tiene certificaciones de obra creadas</p>
                <button 
                  onClick={() => navigate(`/proyectos/${id}/certificaciones/nueva`)}
                  className="btn-primary"
                >
                  ➕ Crear Primera Certificación
                </button>
              </div>
            ) : (
              <>
                <div className="certificaciones-header">
                  <h3>Certificaciones de Obra ({certificaciones.length})</h3>
                  <button 
                    onClick={() => navigate(`/proyectos/${id}/certificaciones/nueva`)}
                    className="btn-primary"
                  >
                    ➕ Nueva Certificación
                  </button>
                </div>

                {resumenCert && (
                  <div className="resumen-certificaciones">
                    <div className="resumen-item">
                      <span className="icon">💰</span>
                      <div>
                        <span className="label">Total Certificado</span>
                        <span className="valor">{formatearMoneda(resumenCert.monto_total_certificado)}</span>
                      </div>
                    </div>
                    <div className="resumen-item">
                      <span className="icon">📋</span>
                      <div>
                        <span className="label">Certificaciones</span>
                        <span className="valor">{resumenCert.total}</span>
                      </div>
                    </div>
                    <div className="resumen-item">
                      <span className="icon">📝</span>
                      <div>
                        <span className="label">Borradores</span>
                        <span className="valor">{resumenCert.borradores || 0}</span>
                      </div>
                    </div>
                    <div className="resumen-item">
                      <span className="icon">✅</span>
                      <div>
                        <span className="label">Aprobadas</span>
                        <span className="valor">{resumenCert.aprobadas || 0}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="cert-grid">
                  {certificaciones.map(cert => (
                    <div key={cert.id} className="cert-card">
                      <div className="cert-header">
                        <div className="cert-numero">
                          <span className="numero-label">Certificación</span>
                          <span className="numero-value">#{cert.numero_certificacion}</span>
                        </div>
                        <span 
                          className="badge"
                          style={{ 
                            backgroundColor: 
                              cert.estado === 'facturada' ? '#10b981' :
                              cert.estado === 'aprobada' ? '#3b82f6' :
                              cert.estado === 'revisión' ? '#f59e0b' : '#6b7280'
                          }}
                        >
                          {cert.estado === 'facturada' ? '💰' :
                           cert.estado === 'aprobada' ? '✅' :
                           cert.estado === 'revisión' ? '🔍' : '📝'} {cert.estado}
                        </span>
                      </div>

                      <div className="cert-body">
                        <div className="cert-info-row">
                          <span className="info-label">📅 Fecha:</span>
                          <span className="info-value">{formatearFecha(cert.fecha)}</span>
                        </div>

                        {cert.periodo_inicio && cert.periodo_fin && (
                          <div className="cert-info-row">
                            <span className="info-label">📆 Período:</span>
                            <span className="info-value">
                              {formatearFecha(cert.periodo_inicio)} - {formatearFecha(cert.periodo_fin)}
                            </span>
                          </div>
                        )}

                        <div className="cert-info-row">
                          <span className="info-label">📦 Partidas:</span>
                          <span className="info-value">{cert.partidas?.length || 0}</span>
                        </div>

                        <div className="cert-monto">
                          <span className="monto-label">Monto Certificado</span>
                          <span className="monto-value">{formatearMoneda(cert.monto_total)}</span>
                        </div>

                        {cert.factura && (
                          <div className="cert-factura">
                            💰 Factura: {cert.factura.numero_factura} - {cert.factura.ncf}
                          </div>
                        )}

                        {cert.observaciones && (
                          <div className="cert-observaciones">
                            📝 {cert.observaciones}
                          </div>
                        )}
                      </div>

                      <div className="cert-footer">
                      <button
                        onClick={(e) => {
                         e.stopPropagation();
                          navigate(`/certificaciones/${cert.id}`);
                          }}
                          className="btn-secondary"
                          >
                         👁️ Ver Detalle
                       </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}


        {vistaActiva === "info" && (
          <div className="info-section">
            <div className="info-grid">
              <div className="info-card">
                <h3>Fechas</h3>
                <div className="info-item">
                  <span className="label">Inicio:</span>
                  <span>{formatearFecha(proyecto.fecha_inicio)}</span>
                </div>
                <div className="info-item">
                  <span className="label">Fin:</span>
                  <span>{formatearFecha(proyecto.fecha_fin)}</span>
                </div>
                <div className="info-item">
                  <span className="label">Duración:</span>
                  <span>
                    {proyecto.fecha_fin
                      ? Math.ceil(
                          (new Date(proyecto.fecha_fin) - new Date(proyecto.fecha_inicio)) /
                            (1000 * 60 * 60 * 24)
                        ) + " días"
                      : "Sin definir"}
                  </span>
                </div>
              </div>

              <div className="info-card">
                <h3>Progreso</h3>
                <div className="progreso-grande">
                  <div className="progreso-numero">{proyecto.progreso}%</div>
                  <div className="progreso-bar-grande">
                    <div
                      className="progreso-fill-grande"
                      style={{
                        width: `${proyecto.progreso}%`,
                        backgroundColor: getEstadoColor(proyecto.estado)
                      }}
                    ></div>
                  </div>
                </div>
              </div>


              {proyecto.descripcion && (
                <div className="info-card full">
                  <h3>Descripción</h3>
                  <p>{proyecto.descripcion}</p>
                </div>
              )}

              {proyecto.notas && (
                <div className="info-card full">
                  <h3>Notas</h3>
                  <p>{proyecto.notas}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal de Detalle de Salida */}
      {salidaSeleccionada && (
        <div style={modalStyles.overlay} onClick={() => setSalidaSeleccionada(null)}>
          <div style={modalStyles.content} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div style={modalStyles.header}>
              <h2 style={modalStyles.title}>📤 Detalle de Salida</h2>
              <button 
                style={modalStyles.btnCerrar} 
                onClick={() => setSalidaSeleccionada(null)}
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div style={modalStyles.body}>
              <div style={modalStyles.grid}>
                <div style={modalStyles.item}>
                  <span style={modalStyles.label}>Material:</span>
                  <span style={modalStyles.value}>
                    {salidaSeleccionada.material?.nombre || '-'}
                  </span>
                </div>

                <div style={modalStyles.item}>
                  <span style={modalStyles.label}>Categoría:</span>
                  <span style={modalStyles.value}>
                    {salidaSeleccionada.material?.categoria?.nombre || '-'}
                  </span>
                </div>

                <div style={modalStyles.item}>
                  <span style={modalStyles.label}>Cantidad:</span>
                  <span style={modalStyles.value}>
                    {salidaSeleccionada.cantidad}
                  </span>
                </div>

                <div style={modalStyles.item}>
  <span style={modalStyles.label}>Fecha de Aprobación:</span>
  <span style={modalStyles.value}>
    {formatearFecha(salidaSeleccionada.fecha_aprobacion || salidaSeleccionada.fecha)}
  </span>
</div>

                <div style={modalStyles.item}>
                  <span style={modalStyles.label}>Responsable:</span>
                  <span style={modalStyles.value}>
                    {salidaSeleccionada.responsable || '-'}
                  </span>
                </div>

                <div style={modalStyles.item}>
                  <span style={modalStyles.label}>Destino:</span>
                  <span style={modalStyles.value}>
                    {salidaSeleccionada.destino || '-'}
                  </span>
                </div>

                <div style={modalStyles.item}>
                  <span style={modalStyles.label}>Valor Total:</span>
                  <span style={modalStyles.value}>
                    {formatearMoneda(salidaSeleccionada.valor_total)}
                  </span>
                </div>

                <div style={modalStyles.item}>
                  <span style={modalStyles.label}>Estado:</span>
                  <span style={{
                    ...modalStyles.badge,
                    backgroundColor: getEstadoColor(salidaSeleccionada.estado)
                  }}>
                    {salidaSeleccionada.estado}
                  </span>
                </div>

                {salidaSeleccionada.observacion && (
                  <div style={{...modalStyles.item, gridColumn: '1 / -1'}}>
                    <span style={modalStyles.label}>Observación:</span>
                    <span style={modalStyles.value}>
                      {salidaSeleccionada.observacion}
                    </span>
                  </div>
                )}

                {/* Proyecto - Card Azul */}
                {salidaSeleccionada.proyecto && (
                  <div style={{
                    gridColumn: '1 / -1',
                    marginTop: '1rem',
                    padding: '1.5rem',
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                    borderRadius: '12px',
                    color: 'white'
                  }}>
                    <div style={{ marginBottom: '0.75rem', fontWeight: 700, fontSize: '1.1rem' }}>
                      🏗️ Proyecto Asignado
                    </div>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'auto 1fr', 
                      gap: '0.75rem', 
                      fontSize: '0.95rem' 
                    }}>
                      <span style={{ opacity: 0.9 }}>Proyecto:</span>
                      <strong>{salidaSeleccionada.proyecto.nombre}</strong>
                      
                      {salidaSeleccionada.proyecto.ubicacion && (
                        <>
                          <span style={{ opacity: 0.9 }}>📍 Ubicación:</span>
                          <span>{salidaSeleccionada.proyecto.ubicacion}</span>
                        </>
                      )}
                      
                      {salidaSeleccionada.proyecto.responsable && (
                        <>
                          <span style={{ opacity: 0.9 }}>👤 Responsable:</span>
                          <span>{salidaSeleccionada.proyecto.responsable}</span>
                        </>
                      )}
                      
                      <span style={{ opacity: 0.9 }}>Estado:</span>
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.75rem',
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        textTransform: 'capitalize'
                      }}>
                        {salidaSeleccionada.proyecto.estado}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Estilos del modal
const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    backdropFilter: 'blur(4px)',
  },
  content: {
    background: 'white',
    borderRadius: '20px',
    width: '90%',
    maxWidth: '650px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },
  header: {
    padding: '24px 32px',
    background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
    color: 'white',
    borderRadius: '20px 20px 0 0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    margin: 0,
    fontSize: '22px',
    fontWeight: '700',
    color: 'white',
  },
  btnCerrar: {
    background: 'rgba(255, 255, 255, 0.2)',
    border: 'none',
    fontSize: '20px',
    color: 'white',
    cursor: 'pointer',
    padding: '8px',
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    transition: 'all 0.2s',
  },
  body: {
    padding: '32px',
    background: '#f8fafc',
    borderRadius: '0 0 20px 20px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '20px',
    background: 'white',
    padding: '24px',
    borderRadius: '16px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  },
  item: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '11px',
    color: '#6b7280',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
  },
  value: {
    fontSize: '15px',
    color: '#1f2937',
    fontWeight: '600',
    lineHeight: '1.4',
  },
  badge: {
    display: 'inline-block',
    padding: '6px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '700',
    color: 'white',
    textTransform: 'uppercase',
  }
};

export default DetalleProyecto;